import { Formik, FormikHelpers, FormikProps } from "formik";
import { useCallback, useContext, useState } from "react";
import { ObjectSchema } from "yup";
import {
  PortalAdditionalReportParamViewModel,
  PortalAdditionalReportRequestViewModel,
  PortalAdditionalReportViewModel,
} from "../../../../generated/data-contracts";
import { ErrorUtils, TimeUtils, api } from "../../../../utils";
import { AvisLoader } from "../../../shared/components";
import { AvisConfirmButton } from "../../../shared/components/buttons";
import { AvisSection } from "../../../shared/components/form";
import { AvisContext } from "../../../shared/context/AvisContext";
import { SelectedReportParamBox } from "./SelectedReportParamBox";
import { selectedReportValidation } from "./SelectedReportValidation";

interface ISelectedReportDetailsProps {
  selectedReport: PortalAdditionalReportViewModel | undefined;
  onParamChange: (pKey: string, pValue: string) => void;
  selectedParams: Record<string, string>;
}

export function SelectedReportDetails(
  props: Readonly<ISelectedReportDetailsProps>,
) {
  const { selectedReport, onParamChange, selectedParams } = props;
  const { res, ctxRightsHolder } = useContext(AvisContext);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [fieldValidationTexts, setFieldValidationTexts] = useState<
    Record<string, string>
  >({});

  const hasAnyYearParam =
    selectedReport?.parameters &&
    selectedReport.parameters.filter((value) => {
      return value.paramType === "Year";
    }).length > 0;

  const validationSchema:
    | ObjectSchema<PortalAdditionalReportRequestViewModel>
    | undefined = selectedReportValidation(
    selectedReport,
    hasAnyYearParam,
    setFieldValidationTexts,
    res,
  );
  const onParamChanging = (pKey: string, pValue: string) => {
    onParamChange(pKey, pValue);

    if (!hasAnyYearParam && selectedReport?.parameters) {
      //After changed dates - revalidate
      validateDateParams(selectedReport.parameters);
    }
  };
  const validateDateParams = (
    reportParameters: PortalAdditionalReportParamViewModel[],
  ) => {
    if (!hasAnyYearParam) {
      for (let i = 0; i < reportParameters.length; i++) {
        if (i > 0) {
          const dateIPrev = TimeUtils.getMomentExactFormat(
            selectedParams[reportParameters[i - 1].reportParameter],
          );
          const dateI = TimeUtils.getMomentExactFormat(
            selectedParams[reportParameters[i].reportParameter],
          );
          if (dateI < dateIPrev) {
            const errorText = res(
              "validation_mustBeGreaterOrEqual",
              reportParameters[i].paramName,
              reportParameters[i - 1].paramName,
            );

            setFieldValidationTexts((prevState) => {
              return {
                ...prevState,
                [reportParameters[i].reportParameter]: errorText,
              };
            });
            return false;
          }
          if (!dateI.isValid()) {
            setFieldValidationTexts((prevState) => {
              return {
                ...prevState,
                [reportParameters[i].reportParameter]: res(
                  "authors_ReportsPage_ReportDetails_validation_chooseDate",
                ),
              };
            });
          }
        }
      }
    }
    setFieldValidationTexts({});
    return true;
  };
  const handleSubmit = (
    values: PortalAdditionalReportRequestViewModel,
    formikHelpers: FormikHelpers<PortalAdditionalReportRequestViewModel>,
  ) => {
    if (!selectedReport?.relativeUrl) return;
    if (!selectedReport?.parameters) return;
    if (!validateDateParams(selectedReport.parameters)) {
      return;
    }

    setIsSubmitting(true);
    api
      .reportsDownloadPortalAdditionalReportFile(values, {
        format: "arraybuffer",
      })
      .then((response) => {
        // create file link in browser's memory
        const fileType = response.headers["content-type"];
        const blob = new Blob([response.data as BlobPart], {
          type: fileType,
        });
        if (!values?.reportParams) return;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;

        const hasYearProperty = selectedReport?.parameters?.find(
          (p) => p.reportParameter === "Year",
        );
        a.download = hasYearProperty
          ? `${selectedReport.name}-${values?.reportParams["Year"]}.pdf`
          : `${selectedReport.name}.pdf`;

        a.click();
        window.URL.revokeObjectURL(url);
        ErrorUtils.showSuccessfulMessage(
          res("message_Success"),
          res("authors_ReportsPage_ReportDetails_messageDownloadSuccess"),
        );
      })
      .catch((err) => ErrorUtils.handleFormValidation(err, formikHelpers))
      .finally(() => setIsSubmitting(false));
  };

  const renderReportDetailsForm = useCallback(
    (formikProps: FormikProps<PortalAdditionalReportRequestViewModel>) => {
      return (
        <AvisSection id="selectedReportDetails" title={selectedReport?.name}>
          <div>{selectedReport?.description}</div>
          <SelectedReportParamBox
            bag={formikProps}
            selectedReport={selectedReport}
            onParamChange={onParamChanging}
            isSubmitting={isSubmitting}
            fieldValidationTexts={fieldValidationTexts}
          />
          <AvisConfirmButton
            label={res("authors_ReportsPage_ReportDetails_btnPrepareReport")}
            onClick={formikProps.submitForm}
            disabled={isSubmitting}
          />
          {isSubmitting && <AvisLoader inline />}
        </AvisSection>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSubmitting, res, selectedReport, fieldValidationTexts],
  );
  if (!selectedReport || !ctxRightsHolder?.rightsHolderId) return;

  const getDefaultValues = () => {
    const defaultValues: Record<string, string> = {};
    if (selectedReport.parameters) {
      selectedReport.parameters.forEach((element) => {
        if (selectedParams[element.reportParameter]) {
          defaultValues[element.reportParameter] =
            selectedParams[element.reportParameter];
        }
      });
    }
    return defaultValues;
  };

  return (
    <div className="selected-report-details">
      <Formik<PortalAdditionalReportRequestViewModel>
        initialValues={{
          rightsHolderId: ctxRightsHolder?.rightsHolderId,
          reportParams: getDefaultValues(),
          relativeUrl: selectedReport.relativeUrl,
        }}
        enableReinitialize={false}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {renderReportDetailsForm}
      </Formik>
    </div>
  );
}
